/**
 * `/leads` — admin Lead list + filter bar + detail drawer (#21).
 *
 * Layout:
 *
 *   - Heading row     — page title + description.
 *   - Filter bar      — site dropdown, status dropdown, date range,
 *                        search input. State is controlled here; changes
 *                        reset `page` to 1.
 *   - Table (md+)     — rows render status chip, name (or "(unnamed)"),
 *                        email, site name, createdAt (relative). Click a
 *                        row to open the detail drawer.
 *   - Card list (xs)  — same data as the table, stacked vertically. The
 *                        narrow viewport doesn't have horizontal room for
 *                        a 5-column table.
 *   - Pagination      — server-side via TanStack Query (paginated meta
 *                        from `/api/admin/leads`).
 *
 * Drawer:
 *   - Right-anchored. Full-width on xs/sm, 480px on desktop.
 *   - Refetches the lead by id (the list response omits formJson + variant
 *     name to keep payloads small).
 *
 * Responsive breakpoints:
 *   - 360px / xs    — card list, drawer fills full width.
 *   - 768px / sm-md — same card list (table activates from md+).
 *   - 1280px / lg+  — table view.
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  LEAD_STATUSES,
  type LeadAdminResponse,
  type LeadStatus,
  type SiteResponse,
} from "@ottopilot/shared";
import { useLeads } from "../hooks/useLeads";
import { useSites } from "../hooks/useSites";
import { LeadStatusChip } from "../features/leads/leadStatusChip";
import { LeadDetailDrawer } from "../features/leads/LeadDetailDrawer";
import { formatRelativeTime } from "../features/leads/relativeTime";

/**
 * Build a site-id → site-name lookup from the sites list payload. The
 * leads table renders the name (the lead carries only `siteId`).
 */
function indexSitesByName(sites: SiteResponse[] | undefined): Map<string, string> {
  const out = new Map<string, string>();
  if (!sites) return out;
  for (const s of sites) out.set(s.id, s.name);
  return out;
}

function LeadRow({
  lead,
  siteName,
  onClick,
}: {
  lead: LeadAdminResponse;
  siteName: string | undefined;
  onClick: () => void;
}): ReactElement {
  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{ cursor: "pointer" }}
      tabIndex={0}
      data-testid="leads-list-row"
      data-lead-id={lead.id}
    >
      <TableCell>
        <LeadStatusChip status={lead.status} />
      </TableCell>
      <TableCell data-testid="leads-list-row-name">
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {lead.name ?? "(unnamed)"}
        </Typography>
      </TableCell>
      <TableCell data-testid="leads-list-row-email">
        <Typography variant="body2" color="text.secondary">
          {lead.email ?? "—"}
        </Typography>
      </TableCell>
      <TableCell data-testid="leads-list-row-site">
        <Typography variant="body2" color="text.secondary">
          {siteName ?? lead.siteId}
        </Typography>
      </TableCell>
      <TableCell data-testid="leads-list-row-created">
        <Typography variant="body2" color="text.secondary">
          {formatRelativeTime(lead.createdAt)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function LeadCard({
  lead,
  siteName,
  onClick,
}: {
  lead: LeadAdminResponse;
  siteName: string | undefined;
  onClick: () => void;
}): ReactElement {
  return (
    <Card variant="outlined" data-testid="leads-list-card">
      <CardActionArea onClick={onClick} data-lead-id={lead.id}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
                {lead.name ?? "(unnamed)"}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {lead.email ?? "—"}
              </Typography>
            </Box>
            <LeadStatusChip status={lead.status} />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1 }}
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Typography variant="caption" color="text.secondary">
              {siteName ?? lead.siteId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(lead.createdAt)}
            </Typography>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export function LeadsPage(): ReactElement {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "">("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);

  /**
   * Convert the operator's plain-date inputs (YYYY-MM-DD) into ISO
   * strings the API consumes. `from` is start-of-day in UTC; `to` is
   * end-of-day + 1 (the API uses an EXCLUSIVE upper bound, so adding a
   * day inside this transformation gives the operator the natural
   * "include this whole day" semantics).
   */
  const fromIso = useMemo(() => {
    if (!fromDate) return undefined;
    const d = new Date(`${fromDate}T00:00:00.000Z`);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }, [fromDate]);
  const toIso = useMemo(() => {
    if (!toDate) return undefined;
    const d = new Date(`${toDate}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return undefined;
    // +1 day to make the operator's `to` filter inclusive of the chosen day.
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString();
  }, [toDate]);

  const sites = useSites({ page: 1, pageSize: 100 });
  const sitesByName = useMemo(() => indexSitesByName(sites.data?.data), [sites.data?.data]);

  const leads = useLeads({
    page,
    pageSize,
    siteId: siteFilter || undefined,
    status: statusFilter || undefined,
    from: fromIso,
    to: toIso,
    q: search.trim() || undefined,
  });

  const handleResetPageOnFilterChange = () => setPage(1);

  return (
    <Stack spacing={3} data-testid="leads-page">
      <Box>
        <Typography variant="h2" data-testid="leads-page-heading">
          Leads
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Every lead captured across your sites. Filter, then click a row to view details and update
          status.
        </Typography>
      </Box>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="leads-filter-bar">
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
            <InputLabel id="leads-filter-site-label">Site</InputLabel>
            <Select
              labelId="leads-filter-site-label"
              label="Site"
              value={siteFilter}
              onChange={(e) => {
                setSiteFilter(e.target.value);
                handleResetPageOnFilterChange();
              }}
              data-testid="leads-filter-site"
            >
              <MenuItem value="">
                <em>All sites</em>
              </MenuItem>
              {sites.data?.data.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
            <InputLabel id="leads-filter-status-label">Status</InputLabel>
            <Select<LeadStatus | "">
              labelId="leads-filter-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                handleResetPageOnFilterChange();
              }}
              data-testid="leads-filter-status"
            >
              <MenuItem value="">
                <em>All statuses</em>
              </MenuItem>
              {LEAD_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              handleResetPageOnFilterChange();
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            data-testid="leads-filter-from"
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              handleResetPageOnFilterChange();
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            data-testid="leads-filter-to"
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="Search name / email / company"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              handleResetPageOnFilterChange();
            }}
            data-testid="leads-filter-search"
            sx={{ flex: 2, minWidth: 220 }}
          />
        </Stack>
      </Paper>

      {leads.isError ? (
        <Alert severity="error" data-testid="leads-list-error">
          {leads.error.message}
        </Alert>
      ) : null}

      {/* Table view (md+) */}
      <Paper variant="outlined" sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer>
          <Table aria-label="Leads" data-testid="leads-list-table">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Captured</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.isPending ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : leads.data && leads.data.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 6 }}
                    data-testid="leads-list-empty"
                  >
                    <Typography variant="body2" color="text.secondary">
                      No leads match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.data?.data.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    siteName={sitesByName.get(lead.siteId)}
                    onClick={() => setOpenLeadId(lead.id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {leads.data?.meta ? (
          <TablePagination
            component="div"
            count={leads.data.meta.total}
            page={page - 1}
            onPageChange={(_e, next) => setPage(next + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        ) : null}
      </Paper>

      {/* Card list view (xs/sm) */}
      <Box sx={{ display: { xs: "block", md: "none" } }} data-testid="leads-list-cards">
        {leads.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : leads.data && leads.data.data.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 6, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No leads match your filters.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {leads.data?.data.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                siteName={sitesByName.get(lead.siteId)}
                onClick={() => setOpenLeadId(lead.id)}
              />
            ))}
            {leads.data?.meta ? (
              <Paper variant="outlined">
                <TablePagination
                  component="div"
                  count={leads.data.meta.total}
                  page={page - 1}
                  onPageChange={(_e, next) => setPage(next + 1)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </Paper>
            ) : null}
          </Stack>
        )}
      </Box>

      <LeadDetailDrawer leadId={openLeadId} onClose={() => setOpenLeadId(null)} />
    </Stack>
  );
}
